import { useQuery } from '@tanstack/react-query';
import id from 'air-datepicker/locale/id';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cctvStreamService, fetchCctvList } from '@/domains/traffic-monitoring/apis/cctv-stream';
import VideoCard from '@/domains/traffic-monitoring/components/VideoCard/VideoCard';
import type { CctvListItem } from '@/domains/traffic-monitoring/types/cctv.types';
import { cn } from '@/lib/utils';
import Header from '@/shared/components/layout/Header/Header';
import CheckBox from '@/shared/components/ui/CheckBox/CheckBox';
import Pagination from '@/shared/components/ui/Pagination/Pagination';
import { useColorStore } from '@/store/colorStore';

const PAGE_SIZE = 6;

const DEVICE_STATUS_TO_TYPE: Record<string, 'online' | 'offline' | 'maintenance'> = {
  DEV_ST_ON: 'online',
  DEV_ST_OFF: 'offline',
  DEV_ST_MTN: 'maintenance',
};

const pageButtonClass = (active: boolean, disabled = false) =>
  cn(
    'min-w-[32px] h-[32px] px-2 rounded-md text-sm font-medium cursor-pointer select-none border',
    active ? 'text-white' : 'bg-db-card text-text-grey border-[#E6E6E6] hover:opacity-40',
    disabled && 'opacity-40 bg-[#E6E6E6] cursor-not-allowed hover:bg-white'
  );

const CCTVDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { NEWCOLOR } = useColorStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // Both kept in the URL (?page=, ?q=) rather than plain useState so they
  // survive navigating to CCTVDetailDashboard and back - a route change
  // unmounts this component, which would otherwise reset them every time.
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const search = searchParams.get('q') ?? '';
  const setPage = useCallback(
    (next: number | ((prev: number) => number)) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          const resolved = typeof next === 'function' ? next(page) : next;
          if (resolved <= 1) {
            params.delete('page');
          } else {
            params.set('page', String(resolved));
          }
          return params;
        },
        { replace: true }
      );
    },
    [page, setSearchParams]
  );

  // Fetched once - the site-list JSON itself is cheap. Bandwidth is only
  // spent on the actual HLS video, which only starts once a VideoCard for
  // that camera is mounted, so paginating to 6-per-page (below) is what
  // keeps the client from opening 20+ concurrent streams at once. Since the
  // full list already lives in memory, search below is just a client-side
  // filter over it - no extra request per keystroke.
  const { data, isLoading } = useQuery({
    queryKey: ['cctvListData'],
    queryFn: () => fetchCctvList(),
    staleTime: 1000 * 60 * 1,
  });

  const cctvList: CctvListItem[] = data?.data?.cctv_list ?? [];
  console.log('cctvList', cctvList);

  const onlineCount = cctvList.filter((c) => c.device_status === 'DEV_ST_ON').length;
  const offlineCount = cctvList.filter((c) => c.device_status === 'DEV_ST_OFF').length;
  const maintenanceCount = cctvList.filter((c) => c.device_status === 'DEV_ST_MTN').length;

  const [selectCheck, setSelectCheck] = useState<string[]>(() => {
    const saved = sessionStorage.getItem('cctv-checks');
    return saved ? JSON.parse(saved) : [];
  });

  // First load (nothing saved yet from a previous visit)
  const hasInitializedChecks = useRef(sessionStorage.getItem('cctv-checks') !== null);
  useEffect(() => {
    if (!hasInitializedChecks.current && cctvList.length > 0) {
      setSelectCheck(cctvList.map((c) => String(c.cctv_id)));
      hasInitializedChecks.current = true;
    }
  }, [cctvList]);

  useEffect(() => {
    sessionStorage.setItem('cctv-checks', JSON.stringify(selectCheck));
  }, [selectCheck]);

  const allCctvIds = useMemo(() => cctvList.map((c) => String(c.cctv_id)), [cctvList]);
  const isAllChecked = allCctvIds.length > 0 && allCctvIds.every((id) => selectCheck.includes(id));

  const handleCheckAll = () => {
    setSelectCheck(isAllChecked ? [] : allCctvIds);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('page');
        return params;
      },
      { replace: true }
    );
  };

  const filteredList = useMemo(() => {
    let list = cctvList;

    // Checkbox - once initialized, selectCheck always reflects exactly the
    // checked cameras, so unchecking everything correctly shows none rather
    // than falling back to "show all".
    if (hasInitializedChecks.current) {
      list = list.filter((c) => selectCheck.includes(String(c.cctv_id)));
    }
    // Search
    const keyword = search.trim().toLowerCase();
    if (keyword) {
      list = list.filter(
        (c) => c.name?.toLowerCase().includes(keyword) || c.address?.toLowerCase().includes(keyword)
      );
    }

    // Device status sort
    list.sort((a, b) => {
      const aIsOffline = a.device_status === 'DEV_ST_OFF';
      const bIsOffline = b.device_status === 'DEV_ST_OFF';

      if (aIsOffline && !bIsOffline) return 1;
      if (!aIsOffline && bIsOffline) return -1;
      return 0;
    });

    return list;
  }, [cctvList, search, selectCheck]);

  const totalPages = Math.max(1, Math.ceil(filteredList.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredList.slice(start, start + PAGE_SIZE);
  }, [filteredList, currentPage]);

  const handleSearchChange = (value: string) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value) {
          params.set('q', value);
        } else {
          params.delete('q');
        }
        params.delete('page');
        return params;
      },
      { replace: true }
    );
  };

  const handleCheck = (cctvId: string) => {
    setSelectCheck((prev) =>
      prev.includes(cctvId) ? prev.filter((id) => id !== cctvId) : [...prev, cctvId]
    );

    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete('page');
        return params;
      },
      { replace: true }
    );
  };

  return (
    <section className="_cctvDashboard-wrap bg-db-wrap flex flex-col gap-[12px] w-full h-screen overflow-hidden">
      <Header />
      <div className="cctv-dashboard-screen p-3 w-full h-[calc(95vh-15px)] grid grid-cols-[300px_1fr] gap-3 items-center overflow-hidden ">
        <div className="w-full h-full bg-db-card bg-db-border border rounded-[12px] p-6 sticky inset-0 overflow-auto">
          <div className="flex flex-col gap-1 ">
            <span className="title1bold">{t('traffic>cctv>camera list', 'Camera List')}</span>
            <span className="title2 text-text-grey">
              {t('traffic>cctv>select cameras to display', 'Select cameras to display.')}
            </span>
          </div>
          <div className="flex flex-col gap-3 mt-6">
            <CheckBox
              id="cctv-select-all"
              label={t('traffic>cctv>select all', 'Select All')}
              checked={isAllChecked}
              onChange={handleCheckAll}
              accentColor={NEWCOLOR.bluePrimary}
            />
            {cctvList.map((cctv) => (
              <div key={cctv.cctv_id}>
                <CheckBox
                  id={String(cctv.cctv_id)}
                  label={cctv.name}
                  checked={selectCheck.includes(String(cctv.cctv_id))}
                  onChange={() => handleCheck(String(cctv.cctv_id))}
                  accentColor={NEWCOLOR.bluePrimary}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 w-full flex flex-col gap-4 h-full border rounded-[12px] bg-db-card bg-db-border overflow-hidden">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div>
                <h2 className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">
                  {t('cmn > camera total', 'Camera Total')}
                </h2>
                <div className="text-2xl font-bold">{cctvList.length}</div>
              </div>
              <div className="flex gap-2 ml-4">
                <div className="px-3 py-1.5 rounded-full border border-green-200 bg-green-50 text-green-700 text-sm font-medium flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500" /> {onlineCount}{' '}
                  {t('traffic>cctv>online', 'ONLINE')}
                </div>
                <div className="px-3 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-700 text-sm font-medium flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500" /> {offlineCount}{' '}
                  {t('traffic>cctv>offline', 'OFFLINE')}
                </div>
              </div>
            </div>
            <div className="relative w-[320px] max-w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('cmn > placeholder input cctv', 'Search CCTV')}
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full h-9 pl-9! pr-4 rounded-full! border border-[#E6E6E6] text-sm placeholder:text-gray-400 outline-none focus:border-gray-400"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="text-center text-gray-400 py-20">
              {t('traffic>cctv>loading cameras', 'Loading cameras…')}
            </div>
          ) : filteredList.length === 0 ? (
            <div className="text-center text-gray-400 py-20">
              {t('traffic>cctv>no cameras found', 'No cameras found.')}
            </div>
          ) : (
            <>
              <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 md:grid-cols-1  gap-6 content-start overflow-auto">
                {pageItems.map((cctv) => (
                  <VideoCard
                    key={cctv.cctv_id}
                    className="w-full h-[250px] "
                    location={cctvStreamService.getStreamLabel(cctv)}
                    showLocation
                    typeVideo={DEVICE_STATUS_TO_TYPE[cctv.device_status] ?? 'maintenance'}
                    streamUrl={cctvStreamService.getHlsUrl(cctv)}
                    onClick={() =>
                      navigate(`/dashboard/cctvDetail-dashboard?cctvId=${cctv.cctv_id}`)
                    }
                  />
                ))}
              </div>

              <div className="flex items-center justify-end gap-[6px]">
                {/* <button
                  type="button"
                  className={pageButtonClass(false, currentPage <= 1)}
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="previous page"
                >
                  <ChevronLeft className="w-4 h-4 mx-auto" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={pageButtonClass(p === currentPage)}
                    style={
                      p === currentPage
                        ? {
                            backgroundColor: NEWCOLOR.bluePrimary,
                            borderColor: NEWCOLOR.bluePrimary,
                          }
                        : undefined
                    }
                    onClick={() => setPage(p)}
                    aria-current={p === currentPage ? 'page' : undefined}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button"
                  className={pageButtonClass(false, currentPage >= totalPages)}
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="next page"
                >
                  <ChevronRight className="w-4 h-4 mx-auto" />
                </button> */}

                <Pagination
                  pageCount={totalPages}
                  currentPage={currentPage}
                  onPageChange={(nextPage) => setPage(nextPage)}
                  nextLabel={<ChevronRight />}
                  previousLabel={<ChevronLeft />}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default CCTVDashboard;
